import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Crown, Swords, Trophy, Vote } from "lucide-react";
import Sidebar, { MobileHeader } from "./Sidebar";
import LatestPatchCard from "./LatestPatchCard";
import { APP_STORE_URL, PLAY_STORE_URL } from "../lib/appStores";
import { isBoyGroupContestOpen } from "../lib/boyGroupLaunch";

const ACCENT = "#F5A623";

const HIGHLIGHTS = [
  {
    icon: Swords,
    title: "Head-to-head",
    body: "Sing, dance, rap, and perform against creators at your level.",
  },
  {
    icon: Vote,
    title: "Community votes",
    body: "Every matchup is decided by the people watching, not an algorithm.",
  },
  {
    icon: Trophy,
    title: "Rank up",
    body: "Wins raise your rank, and a higher rank is how new fans find you.",
  },
];

export default function Landing() {
  const boyGroupOpen = isBoyGroupContestOpen();

  return (
    <div className="relative min-h-screen bg-black text-white">
      <Helmet>
        <title>Pagent: Battle for Fame</title>
        <meta
          name="description"
          content="Pagent is a free talent contest app. Compete in head-to-head matchups, get voted on by the community, and climb the ranks. Download on the App Store and Google Play."
        />
        <link rel="canonical" href="https://www.pagent.tv/" />
      </Helmet>

      <Sidebar />

      <div className="min-h-screen md:pl-60">
        <MobileHeader />

        <main>
          <section className="px-4 pb-6 pt-8 sm:pt-16">
            <div className="mx-auto max-w-3xl text-center">
              <img
                src="/images/pagent-logo-icon.png"
                alt="Pagent"
                className="mx-auto h-20 w-20 rounded-[22.5%] shadow-[0_0_0_1px_rgba(255,255,255,0.12)] sm:h-24 sm:w-24"
              />
              <p
                className="mt-6 text-xs font-bold uppercase tracking-[0.28em]"
                style={{ color: ACCENT }}
              >
                Free on iOS and Android
              </p>
              <h1 className="mt-3 text-4xl font-black tracking-tight text-white sm:text-6xl">
                Battle for{" "}
                <span className="bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">
                  Fame
                </span>
              </h1>
              <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
                Pagent is where creators go head to head. The community votes
                on who wins, and your rank is what gets you seen.
              </p>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-opacity hover:opacity-90"
                >
                  <img
                    src="/images/AppStoreFig.png"
                    alt="Download on the App Store"
                    className="h-12 w-auto sm:h-14"
                  />
                </a>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-opacity hover:opacity-90"
                >
                  <img
                    src="/images/GoogleStoreFig.png"
                    alt="Get it on Google Play"
                    className="h-12 w-auto sm:h-14"
                  />
                </a>
              </div>
            </div>
          </section>

          <section className="px-4 py-10">
            <div className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-3">
              {HIGHLIGHTS.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="rounded-2xl border border-white/10 bg-zinc-900/60 p-5 text-left"
                  >
                    <span
                      className="flex h-11 w-11 items-center justify-center rounded-xl"
                      style={{ backgroundColor: `${ACCENT}22` }}
                    >
                      <Icon className="h-5 w-5" style={{ color: ACCENT }} />
                    </span>
                    <h2 className="mt-4 text-lg font-bold text-white">
                      {item.title}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-white/65">
                      {item.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="px-4 pb-4">
            <div className="mx-auto max-w-5xl space-y-4">
              <Link
                to="/girlgroup"
                className="group relative block overflow-hidden rounded-3xl border border-white/10"
              >
                <img
                  src="/images/girlgroup.png"
                  alt=""
                  className="h-56 w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02] sm:h-72"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/20" />
                <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-10">
                  <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-white">
                    <Crown className="h-3.5 w-3.5" />
                    Contest
                  </span>
                  <h2 className="mt-3 max-w-md text-2xl font-black uppercase leading-tight tracking-tight sm:text-4xl">
                    Pagent Girl Group
                  </h2>
                  <p className="mt-2 max-w-md text-sm text-white/80 sm:text-base">
                    Build the world&apos;s next girl group. One Mission at a
                    time.
                  </p>
                  <span
                    className="mt-4 inline-flex w-fit rounded-full px-4 py-2 text-sm font-extrabold text-black"
                    style={{ backgroundColor: ACCENT }}
                  >
                    Enter now
                  </span>
                </div>
              </Link>

              {boyGroupOpen ? (
                <Link
                  to="/boygroup"
                  className="group relative block overflow-hidden rounded-3xl border border-white/10"
                >
                  <img
                    src="/images/boygroup-thumbnail.jpg"
                    alt=""
                    className="h-56 w-full object-cover object-center transition-transform duration-300 group-hover:scale-[1.02] sm:h-72"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/20" />
                  <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-10">
                    <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-white">
                      <Crown className="h-3.5 w-3.5" />
                      Contest
                    </span>
                    <h2 className="mt-3 max-w-md text-2xl font-black uppercase leading-tight tracking-tight sm:text-4xl">
                      OLYMPUS is RISING
                    </h2>
                    <p className="mt-2 max-w-md text-sm text-white/80 sm:text-base">
                      The first 100% online-voted boy group. Opens November 1st,
                      2026.
                    </p>
                    <span
                      className="mt-4 inline-flex w-fit rounded-full px-4 py-2 text-sm font-extrabold text-black"
                      style={{ backgroundColor: ACCENT }}
                    >
                      Learn more
                    </span>
                  </div>
                </Link>
              ) : null}
            </div>
          </section>

          <section className="px-4 pb-16 pt-12">
            <div className="mx-auto max-w-3xl">
              <div className="mb-5 text-center">
                <h2 className="text-2xl font-bold text-white sm:text-3xl">
                  Latest update
                </h2>
                <p className="mt-2 text-sm text-white/50">
                  Open the note to see what shipped in the app.
                </p>
              </div>
              <LatestPatchCard />
              <p className="mt-4 text-center text-sm">
                <Link
                  to="/updates"
                  className="font-semibold text-white/60 hover:text-white"
                >
                  All patch updates
                </Link>
              </p>
            </div>
          </section>
        </main>

        <footer className="border-t border-white/10 px-4 py-8 text-center text-sm text-white/40">
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link to="/girlgroup" className="hover:text-white/70">
              Girl Group
            </Link>
            {boyGroupOpen ? (
              <Link to="/boygroup" className="hover:text-white/70">
                Boy Group
              </Link>
            ) : null}
            <Link to="/updates" className="hover:text-white/70">
              Patch updates
            </Link>
            <Link to="/faq" className="hover:text-white/70">
              FAQ
            </Link>
            <Link to="/support" className="hover:text-white/70">
              Support
            </Link>
            <Link to="/terms" className="hover:text-white/70">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-white/70">
              Privacy
            </Link>
          </div>
          <p className="mt-3">© 2026 Pagent. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
